import { useEffect } from 'react';
import { useRouter } from '@/i18n/navigation';
import { formatInnerJumpUrl } from '@/utils/jump-url';
import type { ActivityItem } from '@/features/activity/store/list';
import type { ActivitySourceEnum } from '@/const/report';

/**
 * 生成活动目标链接
 * @param item 活动项
 * @param source 来源标识
 * @param withTab 是否带 tab 参数
 * @returns 内部链接或 null(外部链接)
 */
export const getActivityLink = (
  item: ActivityItem,
  source: ActivitySourceEnum,
  withTab = false
): string | null => {
  const { isCommon, activityLink } = item;

  // 外部链接返回 null
  if (isCommon === 0 && activityLink && /^http/.test(activityLink)) {
    return null;
  }

  // 内部链接
  if (isCommon === 0 && activityLink) {
    const params = [`detailId=${item.id}`, `trackSource=${source}`];
    if (withTab) params.push('tab=activity');
    return formatInnerJumpUrl(`${activityLink}?${params.join('&')}`);
  }

  // 默认详情模板页
  const params = [`trackSource=${source}`];
  if (withTab) params.push('tab=activity');
  return formatInnerJumpUrl(`/promo?id=${item.id}&${params.join('&')}`);
};

/**
 * 预加载活动列表的 Hook
 * @param activities 活动列表
 * @param source 来源标识
 * @param options 配置选项
 */
export const usePrefetchActivities = (
  activities: ActivityItem[],
  source: ActivitySourceEnum,
  options: {
    enabled?: boolean;
    withTab?: boolean;
    limit?: number;
  } = {}
) => {
  const { enabled = true, withTab = false, limit } = options;
  const router = useRouter();

  useEffect(() => {
    if (!enabled || !activities || activities.length === 0) return;

    const itemsToPreload = limit ? activities.slice(0, limit) : activities;

    itemsToPreload.forEach((item) => {
      const link = getActivityLink(item, source, withTab);
      if (link) {
        router.prefetch(link);
      }
    });
  }, [activities, source, withTab, limit, enabled, router]);

  return { getActivityLink: (item: ActivityItem) => getActivityLink(item, source, withTab) };
};
